import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Bars3Icon,
  UserCircleIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

interface TopBarProps {
  onToggleSidebar: () => void;
  isMobile?: boolean;
}

const TopBar: React.FC<TopBarProps> = ({ onToggleSidebar }) => {
  const { user, logout } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const navigate = useNavigate();
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Administrator';
      case 'MANAGER':
        return 'Manager';
      case 'FIELD_TEAM':
        return 'Field Team';
      default:
        return role;
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="bg-white h-16 flex items-center justify-between px-3 sm:px-4 md:px-6 shadow-soft border-b border-gray-200 sticky top-0 z-40">
      <div className="flex items-center space-x-3 sm:space-x-4">
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Bars3Icon className="w-6 h-6 text-gray-700" />
        </button>
        <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 truncate">
          Dashboard
        </h1>
      </div>

      <div className="flex items-center space-x-2 sm:space-x-4">

        {/* Profile Menu */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center space-x-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-md">
              {getInitials(user?.name || 'U')}
            </div>
            <div className="hidden sm:block text-left">
              <div className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                {user?.name}
              </div>
              <div className="text-xs text-gray-500">
                {getRoleDisplayName(user?.role || '')}
              </div>
            </div>
            <ChevronDownIcon className="w-4 h-4 text-gray-400 hidden sm:block" />
          </button>

            {showProfileMenu && (
              <div
                className="absolute right-0 mt-2 w-64 sm:w-72 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden"
              >
                <div className="px-4 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
                  <div className="flex items-center space-x-3">
                    <div className="w-14 h-14 bg-white/30 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {getInitials(user?.name || 'U')}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-white truncate">
                        {user?.name}
                      </div>
                      <div className="text-sm text-blue-100 truncate">
                        {user?.email}
                      </div>
                      <div className="text-xs text-white/80 font-medium uppercase tracking-wide mt-1">
                        {getRoleDisplayName(user?.role || '')}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="py-2">
                  <button
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors duration-200"
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate('/profile');
                    }}
                  >
                    <UserCircleIcon className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-700">Profile Settings</span>
                  </button>
                </div>
                
                <div className="border-t border-gray-200">
                  <button 
                    className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center space-x-3 transition-colors duration-200 text-red-600"
                    onClick={handleLogout}
                  >
                    <ArrowRightStartOnRectangleIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default TopBar;